/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    ColumnActionsMode,
    DetailsHeader,
    IColumn,
    IDetailsColumnRenderTooltipProps,
    IDetailsHeaderProps
} from '@fluentui/react/lib/DetailsList';
import { Link } from '@fluentui/react/lib/Link';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Badge } from 'src/core/components/Badge';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { tryParseJson } from 'src/core/utils/base';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { prettyPrintEntity } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { EntityFeaturesCell } from 'src/luis/verticals/entityTable/components/EntityFeaturesCell';

export type EntityTableProps = TableProps<EntityStore> & {
    entities: EntityStore[];
    showFeaturesColumn?: boolean;
    onEntityClick: (entityId: LuisId) => void;
};

const messages = defineMessages({
    nameColumnHeader: {
        id: 'entities.entityTable.nameColumnHeader',
        defaultMessage: 'Name'
    },
    typeColumnHeader: {
        id: 'entities.entityTable.typeColumnHeader',
        defaultMessage: 'Type'
    },
    featuresColumnHeader: {
        id: 'entities.entityTable.featuresColumnHeader',
        defaultMessage: 'Entities used as features'
    },
    emptyTablePlaceholder: {
        id: 'entities.entityTable.emptyTablePlaceholder',
        defaultMessage: "You haven't created any entities yet."
    },
    newBadgeText: {
        id: 'entities.entityTable.newBadgeText',
        defaultMessage: 'New'
    },
    upgradeText: {
        id: 'entities.entityTable.upgradeText',
        defaultMessage: 'Upgrade'
    },
    featuresCalloutText: {
        id: 'entities.entityTable.featuresCalloutText',
        defaultMessage:
            'Machine learning features linked to specifically to this part of the entity. An entity or sub-entity can use one feature as a requirement.'
    }
});

const theme = getScopedTheme('EntityTable');

const { getClassName } = getStylistV2('EntityTable');

const newBadgeClassName = getClassName('newBadgeClassName', {
    padding: '0 2px',
    lineHeight: '16px',
    borderRadius: '3px',
    color: theme.newBadgeTextColor,
    background: theme.newBadgeColor
});

/**
 * Displays a table of entities with their types
 * and features.
 *
 * @param props The input properties for the table.
 */
export const EntityTable = (props: EntityTableProps) => {
    const { items, entities, showFeaturesColumn = false, selection, sortKey, onEntityClick, ...rest } = props;
    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();

    const [migratedEntitiesIds, setMigratedEntitiesIds] = React.useState<string[]>([]);

    React.useEffect(
        () =>
            setMigratedEntitiesIds(
                tryParseJson(getStorage('sessionStorage').getData(LuisConstants.StorageKeys.MigratedEntitiesIdsStorageKey))
            ),
        []
    );

    /**
     * A function to render the entity type cell.
     */
    const renderEntityType = (entity: Entity) => (
        <Stack verticalFill verticalAlign="center" tokens={{ childrenGap: 5 }} horizontal>
            <LuisText color={theme.fontColor}>{prettyPrintEntity(entity.typeId)}</LuisText>

            {migratedEntitiesIds?.includes(entity.id as string) && (
                <Badge text={localize(messages.newBadgeText)} className={newBadgeClassName} />
            )}

            {entity.typeId === ModelType.Composite && (
                <Link
                    data-automation-id="upgradeButton"
                    onClick={() => dispatcher.dispatch('updateEntitiesMigrationModalVisibility', { isVisible: true })}>
                    {localize(messages.upgradeText)}
                </Link>
            )}
        </Stack>
    );

    /**
     * A function to render the entity features cell.
     */
    const renderEntityFeatures = (entity: EntityStore) => <EntityFeaturesCell entity={entity} allEntities={entities} />;

    /**
     * A function to render a callout in the features column header.
     */
    const renderCustomHeaderTooltip = (tooltipHostProps: IDetailsColumnRenderTooltipProps) => {
        const { column } = tooltipHostProps;
        const isFeaturesColumn = column?.key === 'features';

        return (
            <Stack horizontal verticalAlign="center">
                <StackItem grow={!isFeaturesColumn}>{tooltipHostProps.children} </StackItem>
                {isFeaturesColumn && <CalloutButton content={localize(messages.featuresCalloutText)} />}
            </Stack>
        );
    };

    const mainColumns: IColumn[] = [
        {
            key: 'name',
            minWidth: 150,
            maxWidth: 300,
            fieldName: 'name',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.nameColumnHeader),
            isSorted: sortKey && !!(sortKey.key === 'name'),
            isSortedDescending: sortKey && !!(sortKey.key === 'name') && sortKey?.order === 'desc',
            onRender: (item: Entity) => <NameCellLink onClick={() => onEntityClick(item.id)}>{item.name}</NameCellLink>
        },
        {
            key: 'typeId',
            minWidth: 150,
            maxWidth: 300,
            isResizable: true,
            fieldName: 'entityType',
            onRender: renderEntityType,
            name: localize(messages.typeColumnHeader),
            isSorted: sortKey && !!(sortKey.key === 'typeId'),
            isSortedDescending: sortKey && !!(sortKey.key === 'typeId') && sortKey?.order === 'desc'
        }
    ];

    const columns: IColumn[] = showFeaturesColumn
        ? mainColumns.concat([
              {
                  key: 'features',
                  name: localize(messages.featuresColumnHeader),
                  fieldName: 'features',
                  minWidth: 150,
                  maxWidth: 300,
                  isResizable: true,
                  onRender: renderEntityFeatures,
                  columnActionsMode: ColumnActionsMode.disabled
              }
          ])
        : mainColumns;

    return (
        <Table<EntityStore>
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            columns={columns}
            items={items}
            selection={selection}
            onRenderDetailsHeader={(headerProps: IDetailsHeaderProps) => (
                <DetailsHeader {...headerProps} onRenderColumnHeaderTooltip={renderCustomHeaderTooltip} />
            )}
            {...rest}
        />
    );
};
